import React, { FC, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useMatch, useResolvedPath } from 'react-router-dom';
import { getRole } from '@/constant/users/user.constant';
import { authorizeRole } from '@/constant/global/global.constant';
import { APP_BG, APP_HEX, APP_TEXT } from '@/constant/theme/colors.constant';
import _ from 'lodash';

import {
  LuPanelLeftClose,
  LuPanelLeftOpen,
  LuDot,
  LuChevronDown,
  LuSettings, // fallback icon
  LuX,
} from 'react-icons/lu';
import AppButton from '@/components/atoms/AppButton';
import { Text } from '@/components/atoms/AppText';
import { IconType } from 'react-icons';
import { SIDEBAR_PROPS } from '@/constant/layout/layout.constant';
import { tid } from '@/utils/testId';
import { LAYOUT_TEST_IDS as TID } from '@/constant/testIds/layout.testIds';
import shortLogo from '../../assets/images/logo/cantaloupe-trans-logo-icon.png';
import horizontalLogoDark from '../../assets/images/logo/cantaloupe_GA_White.png';
import { MenuItems } from './DefaultNavItem';
import { useTheme } from '@/context/theme.context';

/* ================= TYPES ================= */
export type MenuItem = {
  label: string | null;
  link: string;
  icon?: IconType;
  gap?: boolean;
  subMenu?: Array<{
    label: string | null;
    link: string;
    icon?: IconType;
  }>;
};

type SidebarSection =
  (typeof SIDEBAR_PROPS.SECTIONS)[keyof typeof SIDEBAR_PROPS.SECTIONS];

interface SidebarProps {
  isOpen: boolean;
  isExpandEnabled?: boolean;
  onToggleExpandEnabled?: (enabled: boolean) => void;
  onToggleOpen?: () => void;
  onMobileClose?: () => void;
  menuItem?: MenuItem[];
}

/* ================= UTILS ================= */
const formatLabel = (value: string | null) =>
  value?.trim().replace(/\b\w/g, (l) => l.toUpperCase()) || '';

const getMenuSection = (menu: MenuItem): SidebarSection => {
  const link = menu.link.toLowerCase();
  const label = menu.label?.toLowerCase() || '';

  if (link.includes('dashboard')) return SIDEBAR_PROPS.SECTIONS.OVERVIEW;

  if (
    link.includes('vditransaction') ||
    link.includes('provider') ||
    link.includes('operator')
  )
    return SIDEBAR_PROPS.SECTIONS.VDI_MANAGEMENT;

  if (
    link.includes('smartcooler') ||
    link.includes('coolertransaction') ||
    link.includes('smartcoolertransaction') ||
    link.includes('applogs') ||
    link.includes('alerts')
  )
    return SIDEBAR_PROPS.SECTIONS.SMART_COOLERS;

  if (
    label.includes('market') ||
    label.includes('product') ||
    label.includes('device') ||
    label.includes('sales')
  )
    return SIDEBAR_PROPS.SECTIONS.INVENTORY;

  return SIDEBAR_PROPS.SECTIONS.ADMIN_PANEL;
};

const isPathActive = (pathname: string, path: string) => {
  const normalizedPath = path.split(/[?#]/)[0];

  return pathname === normalizedPath;
};

/* ================= LINK ================= */
const SidebarLink: FC<{
  menu: MenuItem;
  isOpen: boolean;
  index: number;
  onNavigate?: () => void;
}> = ({ menu, isOpen, index, onNavigate }) => {
  const resolved = useResolvedPath(menu.link);
  const match = useMatch({ path: resolved.pathname, end: true });
  const location = useLocation();
  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);

  const hasSubMenu = !!menu.subMenu?.length;
  const isChildActive =
    hasSubMenu &&
    menu.subMenu!.some((sub) => isPathActive(location.pathname, sub.link));
  const subMenuId = `submenu-${menu.link.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`;

  const isActive = isPathActive(location.pathname, resolved.pathname);

  useEffect(() => {
    if (isChildActive) setIsSubmenuOpen(true);
  }, [isChildActive]);

  const label = formatLabel(menu.label);

  /* ✅ SINGLE SOURCE ICON */
  const Icon = menu.icon || LuSettings;

  return (
    <div style={{ transitionDelay: `${index * 40}ms` }}>
      <div className="relative">
        <Link
          to={menu.link}
          data-testid={tid(TID.SIDEBAR_NAV_LINK(menu.label ?? menu.link))}
          onClick={onNavigate}
          className={`
            group relative flex items-center h-10 mx-2.5 rounded-lg
            transition-all duration-300 ease-[cubic-bezier(0.23,1,0.32,1)]
            ${isActive
              ? 'bg-gradient-to-r from-primary via-secondary to-tertiary text-white shadow-lg scale-[1.04]'
              : 'text-slate-400 hover:text-white hover:bg-white/5'
            }
            ${!isOpen ? 'justify-center px-0' : hasSubMenu ? 'px-3 pr-10' : 'px-3'}
          `}
        >
          {/* ACTIVE INDICATOR */}
          {isActive && (
            <span className="absolute -left-1.5 w-1 h-8 rounded-full bg-primary" />
          )}

          {/* ICON */}
          <div
            className={`
              flex items-center justify-center transition-all duration-300
              ${isActive ? 'scale-110' : 'group-hover:scale-110'}
            `}
          >
            <Icon size={20} strokeWidth={2.3} />
          </div>

          {/* LABEL */}
          {isOpen && (
            <Text
              as="span"
              size="base"
              weight="semibold"
              color="inherit"
              tracking="tight"
              className="ml-2.5 flex-1 whitespace-nowrap"
            >
              {label}
            </Text>
          )}

          {/* ACTIVE DOT */}
          {isOpen && isActive && !hasSubMenu && (
            <LuDot size={30} className="animate-pulse" />
          )}

          {/* TOOLTIP (collapsed mode) */}
          {!isOpen && (
            <div className="absolute left-16 opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none z-[110]">
              <div className="bg-black text-white px-3 py-1.5 rounded-md shadow-xl">
                <Text
                  as="span"
                  size="xs"
                  weight="semibold"
                  color="inherit"
                  tracking="widest"
                  className="uppercase"
                >
                  {label}
                </Text>
              </div>
            </div>
          )}
        </Link>

        {/* SUBMENU TOGGLE */}
        {isOpen && hasSubMenu && (
          <button
            type="button"
            onClick={() => setIsSubmenuOpen((prev) => !prev)}
            className={`
              absolute right-3.5 top-2 inline-flex h-6 w-6 items-center justify-center rounded-md
              ${APP_TEXT['white-muted']}
              hover:text-white ${APP_BG.sidebarHover}
              transition-all duration-200
            `}
            aria-label={isSubmenuOpen ? 'Collapse submenu' : 'Expand submenu'}
            aria-expanded={isSubmenuOpen}
            aria-controls={subMenuId}
          >
            <LuChevronDown
              size={16}
              className={`transition-transform duration-300 ${isSubmenuOpen ? 'rotate-180' : 'rotate-0'}`}
            />
          </button>
        )}
      </div>

      {/* SUBMENU ITEMS */}
      {isOpen && hasSubMenu && (
        <div
          id={subMenuId}
          className={`
            ml-6 mt-1 mr-2 grid overflow-hidden transition-all duration-300
            ${isSubmenuOpen ? 'opacity-100' : 'opacity-0'}
          `}
          style={{ gridTemplateRows: isSubmenuOpen ? '1fr' : '0fr' }}
        >
          <div
            className={`
              min-h-0 rounded-xl p-1.5 flex flex-col gap-1
              ${APP_BG.sidebarPopover}
              transition-all duration-300 ${isSubmenuOpen ? 'translate-y-0' : '-translate-y-1'}
            `}
          >
            {menu.subMenu!.map((sub, subIdx) => {
              const isSubActive = isPathActive(location.pathname, sub.link);
              return (
                <Link
                  key={`${menu.link}-${sub.link}`}
                  to={sub.link}
                  onClick={onNavigate}
                  className={`
                    h-9 rounded-lg px-2.5 flex items-center gap-2.5 text-xs
                    transition-all duration-200
                    ${isSubActive
                      ? 'text-white bg-white/10'
                      : `${APP_TEXT['white-muted']} hover:text-white ${APP_BG.sidebarHover}`
                    }
                  `}
                >
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${isSubActive ? 'bg-white' : 'bg-white/30'}`}
                  />
                  <span className="truncate font-medium tracking-wide">
                    {formatLabel(sub.label)}
                  </span>
                  {!isSubActive && subIdx === 0 && (
                    <span
                      className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-md ${APP_TEXT['white-dim']} bg-white/5`}
                    >
                      1
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/* ================= SIDEBAR ================= */
const Sidebar: FC<SidebarProps> = ({
  isOpen,
  isExpandEnabled = false,
  onToggleExpandEnabled,
  onToggleOpen,
  onMobileClose,
  menuItem = MenuItems,
}) => {
  const version = import.meta.env.VITE_APP_VERSION;
  const { colors } = useTheme();

  /* ROLE FILTER */
  const filteredMenuList = useMemo<MenuItem[]>(() => {
    const userHasAccess = _.includes(authorizeRole.PORTAL.DELETE, getRole());

    return (menuItem || []).filter((m) => userHasAccess || m.label !== 'User');
  }, [menuItem]);

  /* GROUPING */
  const groupedMenu = _.groupBy<MenuItem>(
    filteredMenuList,
    getMenuSection,
  ) as Record<SidebarSection, MenuItem[]>;
  const sections: SidebarSection[] = [
    SIDEBAR_PROPS.SECTIONS.OVERVIEW,
    SIDEBAR_PROPS.SECTIONS.VDI_MANAGEMENT,
    SIDEBAR_PROPS.SECTIONS.SMART_COOLERS,
    SIDEBAR_PROPS.SECTIONS.INVENTORY,
    SIDEBAR_PROPS.SECTIONS.ADMIN_PANEL,
  ];

  const logo = horizontalLogoDark;

  return (
    <aside
      data-testid={tid(TID.SIDEBAR)}
      className={`
        relative h-screen flex flex-col shrink-0 z-[100]
        transition-all duration-500 ease-[cubic-bezier(0.2,0.8,0.2,1)]
        will-change-[width]
        ${isOpen ? 'w-60' : 'w-[72px]'}
        ${APP_BG.sidebar} border-r border-white/5
      `}
      style={{
        contain: 'layout style',
        willChange: 'transform',
      }}
    >
      {/* ================= MOBILE CLOSE BUTTON ================= */}
      {onMobileClose && (
        <div className="md:hidden absolute top-3 right-3 z-10">
          <AppButton
            data-testid={tid(TID.SIDEBAR_MOBILE_CLOSE_BTN)}
            variant="neutral"
            shape="square"
            size="sm"
            icon={<LuX size={18} />}
            onClick={onMobileClose}
            aria-label="Close menu"
          />
        </div>
      )}

      {/* ================= LOGO ================= */}
      <div className="flex px-4 mt-3 mb-3">
        <div
          className={`relative flex items-center justify-start md:justify-center transition-all duration-500 ${isOpen ? 'w-full' : 'w-10'
            }`}
        >
          <img
            src={isOpen ? logo : shortLogo}
            alt="logo"
            width="200" // Set these to the natural width of your image
            height="64"
            className={`
              max-w-full object-contain transition-all duration-500
              ${isOpen ? 'h-10 md:h-16 opacity-100' : 'h-[40px] md:h-[56px] opacity-100 scale-110'}
            `}
          />
        </div>
      </div>

      {/* ================= MENU ================= */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden py-1.5 no-scrollbar">
        {sections.map((section) => {
          const items: MenuItem[] = groupedMenu[section] || [];
          if (!items?.length) return null;

          return (
            <div key={section} className="mb-3">
              {/* SECTION TITLE */}
              {isOpen && (
                <div className="flex items-center gap-2 px-4 mb-2">
                  <Text
                    as="span"
                    size="2xs"
                    weight="semibold"
                    color="faint"
                    tracking="superwide"
                    uppercase
                  >
                    {section}
                  </Text>
                  <div className="h-[1px] flex-1 bg-white/5" />
                </div>
              )}

              {/* LINKS */}
              <div className="flex flex-col gap-1">
                {items.map((menu, idx) =>
                  menu.label ? (
                    <SidebarLink
                      key={menu.link}
                      menu={menu}
                      isOpen={isOpen}
                      index={idx}
                      onNavigate={onMobileClose}
                    />
                  ) : null,
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ================= FOOTER ================= */}
      <div className="mt-auto border-t border-white/5">
        {/* Expand Lock */}
        <div className="p-3 hidden md:block">
        {isOpen && (
          <div className="mb-3 px-1 text-[10px] text-slate-500 font-medium">
            v{version}
          </div>
        )}
        {isOpen && (
          <div className="mt-2.5 flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.03] p-2">
            <div className="min-w-0 pr-2">
              <Text
                as="p"
                size="xs"
                weight="semibold"
                color="soft"
                tracking="wide"
              >
                {SIDEBAR_PROPS.HOVER_EXPAND_LOCK_TITLE}
              </Text>
              <Text
                as="p"
                size="2xs"
                color="muted"
                className="leading-tight mt-0.5"
              >
                {isExpandEnabled
                  ? SIDEBAR_PROPS.HOVER_EXPAND_LOCK_ENABLED
                  : SIDEBAR_PROPS.HOVER_EXPAND_LOCK_DISABLED}
              </Text>
            </div>

            <AppButton
              data-testid={tid(TID.SIDEBAR_EXPAND_LOCK_TOGGLE)}
              variant="neutral"
              shape="pill"
              size="xs"
              onClick={() => onToggleExpandEnabled?.(!isExpandEnabled)}
              className="relative !h-6 !w-12 !p-0 flex-shrink-0 cursor-pointer border-0 transition-colors duration-300"
              style={{
                background: isExpandEnabled
                  ? `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`
                  : APP_HEX.slate700,
                boxShadow: isExpandEnabled ? `0 0 0 2px ${colors.primary}33` : 'none',
              }}
              aria-label={SIDEBAR_PROPS.TOGGLE_ARIA_LABEL}
              icon={
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg ring-0 transition-transform duration-300 ${isExpandEnabled ? 'translate-x-3' : '-translate-x-2.5'}`}
                />
              }
            />
          </div>
        )}
      </div>
      </div>

      {/* ================= OVERLAY TOGGLE BUTTON ================= */}
      {isExpandEnabled && (
        <button
          data-testid={tid(TID.SIDEBAR_COLLAPSE_BTN)}
          onClick={onToggleOpen}
          className={`
            absolute bottom-16 -right-[10px] z-[120]
            inline-flex items-center justify-center h-9 w-9 rounded-full
            border border-white/10 ${APP_BG.sidebarPopover} shadow-lg
            transition-all duration-300
            text-slate-300 hover:text-white ${APP_BG.sidebarHover}
          `}
          aria-label={
            isOpen ? SIDEBAR_PROPS.COLLAPSE_LABEL : SIDEBAR_PROPS.EXPAND_LABEL
          }
          title={
            isOpen ? SIDEBAR_PROPS.COLLAPSE_LABEL : SIDEBAR_PROPS.EXPAND_LABEL
          }
        >
          {isOpen ? (
            <LuPanelLeftClose size={18} />
          ) : (
            <LuPanelLeftOpen size={18} />
          )}
        </button>
      )}
    </aside>
  );
};

export default Sidebar;
